import { ArrowRight, Plus, Search } from "lucide-react";
import { useState } from "react";

import { useAddAlias, useAliases, useRepointAlias, useRetireAlias, type Alias } from "@/api/jd-gap-queries";
import { useCan } from "@/auth/member";
import { Button } from "@/components/ui/button";
import { Combobox, Input, Select } from "@/components/ui/input";
import { Empty, ErrorNote, Panel } from "@/components/ui/panel";
import { Switch } from "@/components/ui/switch";
import { cn, formatDate } from "@/lib/utils";

/**
 * Skill aliases — DESIGN §20.5 step 4, Phase 3. What the bank has learned from people correcting a
 * JD's mapping: these words, in a JD, mean this stack. The next JD that writes a skill the same way maps
 * without asking the model. Pointing one elsewhere or retiring it changes the next JD only; skills
 * already mapped keep their stack until someone changes them on their JD.
 */

const TH = "text-bank-muted px-3 py-2 font-mono text-[10.5px] font-medium tracking-[0.07em] uppercase";
type Category = "technical" | "domain";

function AliasRow({ alias, stacks, canManage }: { alias: Alias; stacks: string[]; canManage: boolean }) {
  const repoint = useRepointAlias();
  const retire = useRetireAlias();
  const retired = alias.deactivated_at !== null;
  return (
    <tr className={cn("border-bank-border border-t align-middle", retired && "text-bank-muted")}>
      <td className={cn("px-3 py-2 font-mono text-[13px]", retired && "line-through")}>{alias.alias}</td>
      <td className="px-3 py-2 font-mono text-xs">{alias.category}</td>
      <td className="min-w-52 px-3 py-2">
        {canManage && !retired ? (
          <Combobox
            id={`alias-${alias.id}`}
            className="w-52"
            value={alias.stack_path}
            onChange={(value) => value && repoint.mutate({ id: alias.id, stackPath: value })}
            options={stacks.map((s) => ({ value: s, label: s }))}
          />
        ) : (
          <span className="font-mono text-xs">{alias.stack_path}</span>
        )}
        <ErrorNote error={repoint.error ?? retire.error} />
      </td>
      <td className="px-3 py-2 text-right font-mono text-xs tabular-nums">
        {alias.used_by} skill{alias.used_by === 1 ? "" : "s"}
      </td>
      <td className="text-bank-muted px-3 py-2 text-xs">
        {alias.created_by ?? "—"} · {formatDate(alias.created_at)}
        {retired && (
          <span className="block">
            retired by {alias.deactivated_by ?? "—"} · {formatDate(alias.deactivated_at)}
          </span>
        )}
      </td>
      <td className="px-3 py-2 text-right">
        {canManage && !retired && (
          <Button
            size="sm"
            variant="ghost"
            disabled={retire.isPending}
            title="These words map by the model's suggestion again"
            onClick={() => retire.mutate(alias.id)}
          >
            Retire
          </Button>
        )}
      </td>
    </tr>
  );
}

/** The new alias, as the first row of the table it will join. */
function AddAliasRow({
  technical,
  domain,
  onDone,
}: {
  technical: string[];
  domain: string[];
  onDone: () => void;
}) {
  const add = useAddAlias();
  const [words, setWords] = useState("");
  const [category, setCategory] = useState<Category>("technical");
  const [stack, setStack] = useState("");
  const stacks = category === "technical" ? technical : domain;
  return (
    <tr className="border-bank-border bg-bank-accent-wash border-t align-top">
      <td className="px-3 py-2">
        <Input
          autoFocus
          aria-label="Words as a JD writes them"
          className="w-56 font-mono"
          placeholder="As a JD writes it — k8s"
          value={words}
          onChange={(e) => setWords(e.target.value)}
        />
      </td>
      <td className="px-3 py-2">
        <Select
          aria-label="Type"
          className="w-32"
          value={category}
          onChange={(e) => {
            setCategory(e.target.value as Category);
            setStack("");
          }}
        >
          <option value="technical">technical</option>
          <option value="domain">domain</option>
        </Select>
      </td>
      <td className="px-3 py-2">
        <Combobox
          id="alias-new-stack"
          className="w-52"
          value={stack}
          onChange={setStack}
          options={[
            { value: "", label: "— choose a stack —" },
            ...stacks.map((s) => ({ value: s, label: s })),
          ]}
        />
      </td>
      <td colSpan={2} className="text-bank-muted px-3 py-2 text-xs">
        Kept lower case, without versions — “React (18.3)” and “react” are one alias.
        <ErrorNote error={add.error} />
      </td>
      <td className="px-3 py-2 text-right whitespace-nowrap">
        <Button size="sm" variant="ghost" onClick={onDone}>
          Cancel
        </Button>{" "}
        <Button
          size="sm"
          variant="primary"
          disabled={!words.trim() || !stack || add.isPending}
          onClick={() => add.mutate({ words, category, stack_path: stack }, { onSuccess: onDone })}
        >
          Add
        </Button>
      </td>
    </tr>
  );
}

export default function SkillAliases() {
  const [showRetired, setShowRetired] = useState(false);
  const [search, setSearch] = useState("");
  const [adding, setAdding] = useState(false);
  const aliases = useAliases(showRetired);
  const canManage = useCan("manage_jd");
  const data = aliases.data;
  const query = search.trim().toLowerCase();
  const all = data?.items ?? [];
  const items = all.filter((a) => !query || a.alias.includes(query) || a.stack_path.includes(query));
  const retired = all.filter((a) => a.deactivated_at !== null).length;

  return (
    <div className="flex flex-col gap-3.5 p-4">
      <header className="flex flex-wrap items-end gap-x-4 gap-y-2">
        <div className="flex-[1_1_360px]">
          <h1 className="text-base font-semibold tracking-tight">Skill aliases</h1>
          <p className="text-bank-muted text-[12.5px]">
            How the words a JD uses map to the bank's stacks — learned from people's corrections.
          </p>
        </div>
        {canManage && data && (
          <Button variant="primary" disabled={adding} onClick={() => setAdding(true)}>
            <Plus className="size-4" aria-hidden="true" />
            Add alias
          </Button>
        )}
      </header>

      <Panel className="flex flex-wrap items-center gap-x-5 gap-y-3 px-4 py-3.5">
        <div className="flex items-center gap-2.5" aria-hidden="true">
          <span className="border-bank-border bg-bank-surface-2 rounded-md border px-2.5 py-1 font-mono text-[13px]">
            “K8s”
          </span>
          <ArrowRight className="text-bank-muted size-4" />
          <span className="bg-bank-accent-wash text-bank-accent-ink rounded-md px-2.5 py-1 font-mono text-[13px]">
            kubernetes
          </span>
        </div>
        <p className="flex-[1_1_420px] text-[13px]">
          When you choose a stack for a skill on a JD, the bank remembers the wording.{" "}
          <span className="text-bank-muted">
            The next JD that says “K8s” maps to kubernetes on its own, without asking the AI. Changing an
            alias affects the next JD only — skills already mapped keep their stack.
          </span>
        </p>
      </Panel>

      <div className="flex flex-wrap items-center gap-3">
        <label className="relative block w-72 max-w-full">
          <Search className="text-bank-muted absolute top-2 left-2.5 size-4" aria-hidden="true" />
          <Input
            type="search"
            className="pl-8"
            aria-label="Search aliases"
            placeholder="Search words or stacks"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </label>
        <div className="flex items-center gap-2 text-[13px]">
          <Switch checked={showRetired} onChange={setShowRetired} label="Show retired aliases" />
          Show retired
        </div>
        <span className="text-bank-muted ml-auto text-[12.5px]">
          {items.length} alias{items.length === 1 ? "" : "es"}
          {showRetired && retired ? ` · ${retired} retired` : ""}
        </span>
      </div>

      <ErrorNote error={aliases.error} />
      <Panel className="overflow-hidden">
        {aliases.isLoading ? (
          <p className="text-bank-muted p-4 text-sm">Loading…</p>
        ) : items.length === 0 && !adding ? (
          <Empty>
            {query
              ? "Nothing matches that."
              : "None yet. Choosing a stack for a skill on a JD's Skills step writes one."}
          </Empty>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-bank-surface-2 border-bank-border border-b text-left">
                <tr>
                  <th className={TH}>Words, as a JD writes them</th>
                  <th className={TH}>Type</th>
                  <th className={TH}>Maps to</th>
                  <th className={`${TH} text-right`}>Used by</th>
                  <th className={TH}>Learned from</th>
                  <th className={TH} />
                </tr>
              </thead>
              <tbody>
                {adding && data && (
                  <AddAliasRow
                    technical={data.technical_stacks}
                    domain={data.domain_stacks}
                    onDone={() => setAdding(false)}
                  />
                )}
                {items.map((a) => (
                  <AliasRow
                    key={a.id}
                    alias={a}
                    stacks={
                      a.category === "technical"
                        ? (data?.technical_stacks ?? [])
                        : (data?.domain_stacks ?? [])
                    }
                    canManage={canManage}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>
    </div>
  );
}
